import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, RefreshCw, ChevronRight } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import DataTable from "@/components/app/DataTable";
import { SubscriptionBadge } from "@/components/app/InvoiceBadge";
import { btnOutline, fmtDate, inr, pageSub, pageTitle } from "@/components/app/ui";

export default function AdminSubscriptions() {
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => api.get("/admin/subscriptions").then(({ data }) => setSubs(data)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const runBilling = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/billing/run");
      toast.success(`Billing check complete: ${data.generated} invoice(s) generated, ${data.lapsed} store(s) moved to Free.`);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setRunning(false); }
  };

  const columns = useMemo(() => [
    { key: "store_name", header: "Store", cell: (s) => (
      <div className="flex items-center gap-2.5 min-w-[160px]">
        <span className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 text-[11px] font-bold flex items-center justify-center shrink-0">{s.store_name?.[0]?.toUpperCase()}</span>
        <div className="min-w-0"><div className="font-semibold text-neutral-900 truncate">{s.store_name}</div><div className="text-[11px] text-slate-500 font-mono">/{s.store_slug}</div></div>
      </div>
    ) },
    { key: "plan_name", header: "Subscribed plan", cell: (s) => <span className="inline-block text-[11px] px-2 py-0.5 rounded-md border bg-blue-50 text-blue-700 border-blue-200 font-medium">{s.plan_name}</span> },
    { key: "effective_plan_name", header: "Current plan", hideBelow: "md", cell: (s) => (
      <span className={s.status === "lapsed" ? "text-red-600 font-semibold" : "text-slate-600"}>{s.effective_plan_name}{s.status === "lapsed" && " (suspended)"}</span>
    ) },
    { key: "amount", header: "Amount", align: "right", accessor: (s) => s.amount || 0, cell: (s) => (
      <span className="font-semibold text-green-600">{inr(s.amount)}<span className="text-slate-400 font-normal">/mo</span></span>
    ) },
    { key: "status", header: "Status", cell: (s) => <SubscriptionBadge status={s.status} /> },
    { key: "outstanding_amount", header: "Outstanding", align: "right", hideBelow: "sm", accessor: (s) => s.outstanding_amount || 0, cell: (s) => (
      s.outstanding_amount ? <span className={`font-semibold ${s.overdue_count ? "text-red-600" : "text-orange-600"}`}>{inr(s.outstanding_amount)}</span> : <span className="text-slate-400">—</span>
    ) },
    { key: "started_at", header: "Since", hideBelow: "lg", accessor: (s) => s.started_at || "", cell: (s) => <span className="text-slate-600 whitespace-nowrap">{fmtDate(s.started_at)}</span> },
    { key: "view", header: "", sortable: false, align: "right", cell: () => <ChevronRight size={16} className="text-slate-400 inline" /> },
  ], []);

  const active = subs.filter((s) => s.status === "active");
  const mrr = active.reduce((n, s) => n + (s.amount || 0), 0);
  const outstanding = subs.reduce((n, s) => n + (s.outstanding_amount || 0), 0);
  const lapsedCount = subs.filter((s) => s.status === "lapsed").length;

  return (
    <div className="space-y-4" data-testid="admin-subscriptions">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className={pageTitle}>Subscriptions</h1>
          <p className={pageSub}>{subs.length} subscriptions. Click a row to see the store&apos;s subscription, stats and invoices.</p>
        </div>
        <button onClick={runBilling} disabled={running} data-testid="admin-run-billing" className={btnOutline} title="Generate due invoices and move stores with overdue invoices to Free">
          <RefreshCw size={14} className={running ? "animate-spin" : ""} /> Run billing check
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Recurring revenue (active)", value: inr(mrr), tone: "from-green-50 to-white border-green-100" },
          { label: "Active subscriptions", value: active.length, tone: "from-blue-50 to-white border-blue-100" },
          { label: "Lapsed (on Free)", value: lapsedCount, tone: "from-red-50 to-white border-red-100" },
          { label: "Outstanding invoices", value: inr(outstanding), tone: "from-orange-50 to-white border-orange-100" },
        ].map((c) => (
          <div key={c.label} className={`rounded-xl border bg-gradient-to-br ${c.tone} px-4 py-3`}>
            <div className="text-[12px] text-slate-500">{c.label}</div>
            <div className="text-[22px] font-bold text-neutral-900 leading-tight">{c.value}</div>
          </div>
        ))}
      </div>

      <DataTable testId="admin-subscriptions-table" exportName="subscriptions" columns={columns} data={subs} loading={loading} rowKey="id"
        rowTestId={(s) => `subscription-row-${s.store_slug}`} onRowClick={(s) => navigate(`/admin/subscriptions/${s.store_id}`)}
        searchKeys={["store_name", "store_slug", "plan_name"]} searchPlaceholder="Search store or plan…"
        filters={[
          { key: "plan", label: "All plans", accessor: (s) => s.plan_name },
          { key: "status", label: "All statuses", accessor: (s) => s.status, options: [{ value: "active", label: "Active" }, { value: "lapsed", label: "Lapsed" }] },
          { key: "outstanding", label: "Any balance", accessor: (s) => (s.overdue_count ? "overdue" : s.outstanding_amount ? "pending" : "clear"), options: [{ value: "clear", label: "No outstanding" }, { value: "pending", label: "Invoice pending" }, { value: "overdue", label: "Overdue" }] },
        ]}
        dateField={{ label: "Started", accessor: (s) => s.started_at }} defaultSort={{ key: "started_at", dir: "desc" }}
        emptyIcon={CreditCard} emptyTitle="No subscriptions yet" emptyText="Subscriptions appear when stores are created." />
    </div>
  );
}
